/* The first screen: the artboard's `authChooser`, in the light register.
 *
 * WHAT IS KEPT FROM THE SCREEN THIS REPLACES, because each was ruled:
 *   - The two tabs change the words only. Every route creates an account if
 *     there is none and finds the one there is.
 *   - A provider button appears only once the auth service says the provider
 *     exists. The list in config is order, not availability.
 *   - Email signs in with a code. Nothing that takes a password ships, so the
 *     artboard's password field is not built.
 *   - "The terms" is not named, because no terms page exists; the privacy
 *     notice is, and it is linked.
 */
import { t } from "@damga/kit";

import { Brand } from "../ui";

export interface SignInMethod {
  id: "phone" | "email" | "apple" | "google";
  label: string;
}

export type AuthMode = "signin" | "signup";

const PROVIDERS = ["apple", "google"] as const;
type ProviderId = (typeof PROVIDERS)[number];

function isProvider(id: SignInMethod["id"]): id is ProviderId {
  return (PROVIDERS as readonly string[]).includes(id);
}

function ProviderMark({ id }: { id: ProviderId }) {
  if (id === "apple") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
          fill="currentColor"
          d="M16.4 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.9-3-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .8 1.1 1.6 2.3 2.8 2.2 1.1 0 1.6-.7 2.9-.7s1.7.7 2.9.7c1.2 0 2-1.1 2.7-2.2.9-1.2 1.2-2.5 1.2-2.5s-2.4-.9-2.4-3.5zM14.2 5.8c.6-.8 1-1.8.9-2.8-.9 0-2 .6-2.6 1.3-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.6-1.2z"
        />
      </svg>
    );
  }
  return <>G</>;
}

export function Welcome({
  methods,
  mode,
  onMode,
  onChoose,
  error,
}: {
  methods: SignInMethod[];
  mode: AuthMode;
  onMode: (m: AuthMode) => void;
  onChoose: (id: SignInMethod["id"]) => void;
  error?: string | null;
}) {
  const providers = methods.filter((m): m is SignInMethod & { id: ProviderId } => isProvider(m.id));
  const phone = methods.find((m) => m.id === "phone");
  const email = methods.find((m) => m.id === "email");
  const signup = mode === "signup";

  return (
    <main className="auth">
      <Brand />

      <div className="seg" role="tablist" aria-label={t("auth.allOptions")}>
        {(["signin", "signup"] as const).map((m) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => onMode(m)}>
            {t(m === "signin" ? "auth.tabSignIn" : "auth.tabSignUp")}
          </button>
        ))}
      </div>

      <div>
        <h1 className="display">{t(signup ? "auth.chooserTitleUp" : "welcome.title")}</h1>
        <p className="lead">{t(signup ? "auth.chooserBodyUp" : "welcome.body")}</p>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {providers.length > 0 && (
        <div className="stack">
          {providers.map((p) => (
            <button
              key={p.id}
              type="button"
              className="provider"
              data-provider={p.id}
              onClick={() => onChoose(p.id)}
            >
              <span className="provider-mark">
                <ProviderMark id={p.id} />
              </span>
              <span>{p.label}</span>
              <span aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {providers.length > 0 && (phone || email) && (
        <p className="or" aria-hidden="true">
          <span>{t("auth.or")}</span>
        </p>
      )}

      {(phone || email) && (
        <div className="stack">
          {phone && (
            <button type="button" className="btn btn-primary" onClick={() => onChoose("phone")}>
              {phone.label}
            </button>
          )}
          {email && (
            <button
              type="button"
              className={phone ? "btn btn-secondary" : "btn btn-primary"}
              onClick={() => onChoose("email")}
            >
              {email.label}
            </button>
          )}
        </div>
      )}

      <p className="fine">
        {t("welcome.terms.lead")}{" "}
        <a href="https://damga.qa/privacy/" target="_blank" rel="noopener noreferrer">
          {t("welcome.terms.link")}
        </a>
        {t("welcome.terms.tail")}
      </p>

      <ul className="promises">
        <li>{t("welcome.noApp")}</li>
        <li>{t("welcome.everyBranch")}</li>
        <li>{t("welcome.yourData")}</li>
      </ul>
    </main>
  );
}
